import { useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  useOpenLayersContext,
  smartZoomTo,
  useDrawCrosshair,
} from '@ais-ui/react-openlayers';
import {
  ShipInfo,
  extractTargetInfos,
  useGetTargetByMMSI,
} from '@ais-ui/websockets';
import { formatVesselName, isValidMMSINumber } from '@ais-ui/utils';
import { fromLonLat } from 'ol/proj';
import { PiArrowArcRightBold, PiPushPinSlashBold } from 'react-icons/pi';
import { useMapPanelContext } from './MapPanelContext';

const REFETCH_INTERVAL_MS = 5_000;

/**
 * Pans the map to keep a followed vessel centred.
 *
 * Renders a small pill at the bottom of the map showing which vessel
 * is being followed and an unfollow button. All polling, coordinate
 * tracking, and backend re-selection logic lives here so the parent
 * component stays clean.
 */
export function FollowShipIndicator({ style }: { style: React.CSSProperties }) {
  const { followMmsi, setFollowMmsi } = useMapPanelContext();
  const { map } = useOpenLayersContext();

  const prevCoordsRef = useRef<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const { data: followData } = useGetTargetByMMSI(followMmsi ?? 0, {
    enabled: followMmsi !== null && isValidMMSINumber(String(followMmsi)),
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  const followedShip = useMemo(() => {
    if (!followData) return undefined;
    return extractTargetInfos(followData).extractedTargetInfo[0] as
      | ShipInfo
      | undefined;
  }, [followData]);

  usePanToFollowedShip(followMmsi, followedShip, map, prevCoordsRef);

  // Draw crosshair on followed ship position
  useDrawCrosshair(
    followMmsi !== null && followedShip ? followedShip.latitude : null,
    followMmsi !== null && followedShip ? followedShip.longitude : null,
    map!,
  );

  if (followMmsi === null) return null;

  const shipName = formatVesselName(followedShip?.name);

  const jumpToShip = () => {
    if (!followedShip || !map) return;
    const lat = followedShip.latitude;
    const lon = followedShip.longitude;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    prevCoordsRef.current = { latitude: lat, longitude: lon };
    const coordinate = fromLonLat([lon, lat]);
    smartZoomTo({ coordinate, map, animate: true });
  };

  return (
    <div
      style={style}
      className="flex justify-center items-end pb-4 pointer-events-none"
    >
      <div className="flex items-center bg-background border rounded-2xl shadow-lg overflow-hidden pointer-events-auto">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Jump to ${shipName || 'ship'}`}
              className="size-10 rounded-none"
              onClick={jumpToShip}
            >
              <PiArrowArcRightBold />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="rounded-xl">
            <p>Jump to {shipName || 'ship'}</p>
          </TooltipContent>
        </Tooltip>
        <Separator orientation="vertical" className="h-6" />
        <span className="px-3 py-1.5 text-sm font-medium">Following</span>
        <Separator orientation="vertical" className="h-6" />
        <span className="px-3 py-1.5 text-sm font-medium">
          {shipName || 'ship'}
        </span>
        <Separator orientation="vertical" className="h-6" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Unfollow ${shipName || 'ship'}`}
              className="size-10 rounded-none"
              onClick={() => setFollowMmsi(null)}
            >
              <PiPushPinSlashBold />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="rounded-xl">
            <p>Unfollow {shipName || 'ship'}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Side-effect hooks – extracted so the render body stays declarative.
// ---------------------------------------------------------------------------

/**
 * Smoothly pans the map whenever the followed vessel moves.
 */
function usePanToFollowedShip(
  followMmsi: number | null,
  followedShip: ShipInfo | undefined,
  map: ReturnType<typeof useOpenLayersContext>['map'],
  prevCoordsRef: React.RefObject<{
    latitude: number;
    longitude: number;
  } | null>,
) {
  useEffect(() => {
    if (followMmsi === null || !followedShip || !map) return;

    const prev = prevCoordsRef.current;
    if (
      followedShip.latitude !== prev?.latitude ||
      followedShip.longitude !== prev?.longitude
    ) {
      const lat = followedShip.latitude;
      const lon = followedShip.longitude;
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

      prevCoordsRef.current = { latitude: lat, longitude: lon };
      const coordinate = fromLonLat([lon, lat]);
      smartZoomTo({ coordinate, map, animate: true });
    }
  }, [followMmsi, followedShip, map, prevCoordsRef]);
}
